import React, { useState, useEffect } from 'react';

import { motion } from 'framer-motion';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';

import CustomSelect from '../../utils/CustomSelect';
import Switcher from '../../utils/Switcher';
import { formatCurrency } from '../../utils/Format';

import EtatsChart from '../../components/charts/EtatsChart';
import Chart3DWrapper from '../../components/charts/Chart3DWrapper';
import DoubleColumnChartDecla from '../../components/charts/DoubleColumnChartDecla';
import MontantChart from '../../components/charts/MontantChart';

const DeclarationTab = ({ declarations, sites, filters, setFilters, loadingD = false, loadingS = false }) => {
    const [filteredData, setFilteredData] = useState([]);

    const [showAmountEtat, setShowAmountEtat] = useState(false);
    const [showAmountCode, setShowAmountCode] = useState(false);
    const [showAmountMotif, setShowAmountMotif] = useState(false);

    const filterDeclarations = (declarations, filters) => {
        let filtered = declarations;

        if (filters.societe !== "Toutes") {
            filtered = filtered.filter(d => d.Societe === filters.societe);
        }
        if (filters.entite !== "Toutes") {
            filtered = filtered.filter(d => d.Entite === filters.entite);
        }
        if (filters.etat !== "Toutes") {
            filtered = filtered.filter(d => d.Etat === filters.etat);
        }
        if (filters.type !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Declaration === filters.type);
        }
        if (filters.typeOuvrage !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Ouvrage === filters.typeOuvrage);
        }
        if (filters.typeDomaineActivite !== "Toutes") {
            filtered = filtered.filter(d => d.Domaine_Activite === filters.typeDomaineActivite);
        }
        if (filters.typeMarche !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Marche === filters.typeMarche);
        }

        if (filters.minDate) {
            filtered = filtered.filter(d => new Date(d.Date_Declaration) >= filters.minDate);
        }
        if (filters.maxDate) {
            filtered = filtered.filter(d => new Date(d.Date_Declaration) <= filters.maxDate);
        }

        if (filters.withCost) {
            filtered = filtered.filter(d => d.Cout_tt_Pathologie > 0 || d.Montant_Du_Definitif > 0);
        }

        if (filters.hideArchived) {
            filtered = filtered.filter(d => !d.Archiver_Enr);
        }

        if (filters.hideCompleted) {
            const completedStates = ["cloture", "sans suite"];
            filtered = filtered.filter(d =>
                !completedStates.some(state =>
                    d.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').startsWith(state)
                )
            );
        }

        return filtered;
    };

    const filterSites = (sites, filters) => {
        let filtered = sites;

        if (filters.societe !== "Toutes") {
            filtered = filtered.filter(d => d.Societe === filters.societe);
        }
        if (filters.entite !== "Toutes") {
            filtered = filtered.filter(d => d.Entite === filters.entite);
        }
        if (filters.etat !== "Toutes") {
            filtered = filtered.filter(d => d.Etat === filters.etat);
        }
        if (filters.type !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Declaration === filters.type);
        }
        if (filters.typeOuvrage !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Ouvrage === filters.typeOuvrage);
        }
        if (filters.typeDomaineActivite !== "Toutes") {
            filtered = filtered.filter(d => d.Domaine_Activite === filters.typeDomaineActivite);
        }
        if (filters.typeMarche !== "Toutes") {
            filtered = filtered.filter(d => d.Type_Marche === filters.typeMarche);
        }

        if (filters.minDate) {
            filtered = filtered.filter(d => new Date(d.Date_PVRecep_String) >= filters.minDate);
        }
        if (filters.maxDate) {
            filtered = filtered.filter(d => new Date(d.Date_PVRecep_String) <= filters.maxDate);
        }

        if (filters.withCost) {
            filtered = filtered.filter(d => d.Cout_tt_Pathologie > 0 || d.Montant_Du_Definitif > 0);
        }

        if (filters.hideArchived) {
            filtered = filtered.filter(d => !d.Archiver_Enr);
        }

        if (filters.hideCompleted) {
            const completedStates = ["cloture", "sans suite"];
            filtered = filtered.filter(d =>
                !completedStates.some(state =>
                    d.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').startsWith(state)
                )
            );
        }

        return filtered;
    };

    useEffect(() => {
        const result = filterDeclarations(declarations, filters);
        setFilteredData(result);
    }, [filters, declarations]);

    const optSociete = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Societe).filter(societe => societe))];
    const optEntite = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Entite).filter(entite => entite))];
    const optEtat = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Etat).filter(etat => etat))];
    const optType = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Declaration).filter(type => type))];
    const optTypeOuvrage = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Ouvrage).filter(type => type))];
    const optTypeMarche = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Marche).filter(type => type))];

    const [top15CoutDeclas, setTop15CoutDeclas] = useState([]);
    const [top15CoutSites, setTop15CoutSites] = useState([]);

    useEffect(() => {
        const filteredDeclas = filterDeclarations(declarations, filters);

        // console.log("Filtered Declarations:", filteredDeclas);

        const mergedCosts = filteredDeclas
            .filter(decla => (decla.Montant_Du_Definitif || decla.Cout_tt_Pathologie))
            .map(decla => ({
                Libelle: decla.Libelle || 'Inconnu',
                SiteNom: decla.SiteNom || 'Inconnu',
                Montant_Du_Definitif: decla.Montant_Du_Definitif || 0,
                Cout_tt_Pathologie: decla.Cout_tt_Pathologie || 0
            }))
            .sort((a, b) =>
                (Math.abs(b.Montant_Du_Definitif) + Math.abs(b.Cout_tt_Pathologie)) -
                (Math.abs(a.Montant_Du_Definitif) + Math.abs(a.Cout_tt_Pathologie))
            )
            .slice(0, 15);

        const top15Costs = [];

        // console.log("Merged Costs:", mergedCosts);

        mergedCosts.forEach(decla => {
            top15Costs.push({
                x: decla.SiteNom || 'Inconnu',
                y: decla.Montant_Du_Definitif,
                category: 'Déclarations',
                libelle: decla.Libelle
            });
            top15Costs.push({
                x: decla.SiteNom || 'Inconnu',
                y: decla.Cout_tt_Pathologie,
                category: 'Pathologies',
                libelle: decla.Libelle
            });
        });

        // console.log("Top 15 Costs:", top15Costs);

        setTop15CoutDeclas(top15Costs);

    }, [sites, declarations, filters]);

    return (
        <>
            <div className="bg-white mt-4 md:mt-8 p-4 rounded-lg">
                <div id="cont_filtre" className="flex xl:flex-row flex-col gap-4 w-full">
                    <div className="flex md:flex-row flex-col gap-4 w-full xl:w-1/2">
                        <div className="w-full">
                            <CustomSelect
                                label={"Société"}
                                options={optSociete}
                                value={filters.societe}
                                onChange={val => setFilters(prev => ({ ...prev, societe: val }))}
                            />
                            <CustomSelect
                                label={"Entité/Région"}
                                options={optEntite}
                                value={filters.entite}
                                onChange={val => setFilters(prev => ({ ...prev, entite: val }))}
                            />
                        </div>
                        <div className="w-full">
                            <CustomSelect
                                label={"État"}
                                options={optEtat}
                                value={filters.etat}
                                onChange={val => setFilters(prev => ({ ...prev, etat: val }))}
                            />
                            <CustomSelect
                                label={"Type de Déclaration"}
                                options={optType}
                                value={filters.type}
                                onChange={val => setFilters(prev => ({ ...prev, type: val }))}
                            />
                        </div>
                    </div>
                    <div className="flex md:flex-row flex-col items-center gap-4 w-full xl:w-1/2 md:full">
                        <div className="w-full">
                            <CustomSelect
                                label={"Type de Marché"}
                                options={optTypeMarche}
                                value={filters.typeMarche}
                                onChange={val => setFilters(prev => ({ ...prev, typeMarche: val }))}
                            />
                            <CustomSelect
                                label={"Type d'Ouvrage"}
                                options={optTypeOuvrage}
                                value={filters.typeOuvrage}
                                onChange={val => setFilters(prev => ({ ...prev, typeOuvrage: val }))}
                            />
                        </div>
                        <div className="flex flex-col justify-between w-full h-full">
                            <div>
                                <span className="text-gray-600 text-sm">À partir du :</span>
                                <DatePickerComponent
                                    placeholder="Trier par Date Minimal"
                                    format={"dd/MM/yyyy"}
                                    value={filters.minDate}
                                    change={(e) => setFilters(prev => ({ ...prev, minDate: e.value }))}
                                />
                            </div>
                            <div>
                                <span className="text-gray-600 text-sm">Jusqu'au :</span>
                                <DatePickerComponent
                                    placeholder="Trier par Date Maximal"
                                    format={"dd/MM/yyyy"}
                                    value={filters.maxDate}
                                    change={(e) => setFilters(prev => ({ ...prev, maxDate: e.value }))}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row xl:flex-col justify-center gap-10 xl:gap-0 w-full xl:w-1/4">
                        <label class="mcui-checkbox">
                            <input type="checkbox" checked={filters.withCost} onChange={() => setFilters(prev => ({ ...prev, withCost: !filters.withCost }))} />
                            <div>
                                <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                    <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                </svg>
                            </div>
                            <div class="text-gray-600 text-lg">Avec Coût</div>
                        </label>
                        <label class="mcui-checkbox">
                            <input type="checkbox" checked={filters.hideArchived} onChange={() => setFilters(prev => ({ ...prev, hideArchived: !filters.hideArchived }))} />
                            <div>
                                <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                    <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                </svg>
                            </div>
                            <div class="text-gray-600 text-lg">Masquer les Archives</div>
                        </label>
                        <label class="mcui-checkbox">
                            <input type="checkbox" checked={filters.hideCompleted} onChange={() => setFilters(prev => ({ ...prev, hideCompleted: !filters.hideCompleted }))} />
                            <div>
                                <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                    <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                </svg>
                            </div>
                            <div class="text-gray-600 text-lg">Masquer les Terminés</div>
                        </label>
                    </div>
                </div>
            </div>
            <div className="lg:flex items-stretch mt-4 md:mt-8">
                <div className="flex flex-col lg:w-1/2 h-full">
                    <div className="sm:flex justify-between items-center gap-x-6 xl:gap-x-8">
                        <div className="relative w-full">
                            <div className="bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 rounded-lg w-full">
                                <div class="p-5 h-full">
                                    <div class="flex justify-between">
                                        <div class="text-white text-center">
                                            <i class="fa-solid fa-scroll fa-lg"></i>
                                        </div>
                                        <div
                                            class={`flex justify-items-center px-2 rounded-full h-6 font-semibold text-white bg-white text-sm ${loadingD ? 'animate-pulse' : ''}`}>
                                            {/* <span class="flex items-center">30%</span> */}
                                        </div>
                                    </div>
                                    <div class="flex-1 ml-2 w-full">
                                        <div>
                                            <div class="mt-3 font-bold text-white text-3xl leading-8">{loadingD ? <div class="bg-gray-100 rounded-md w-20 h-8 animate-pulse"></div> : filteredData.length}</div>

                                            <div class="mt-1 text-gray-200 text-base">Déclarations</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="relative mt-4 md:mt-6 w-full">
                        <motion.div
                            className="bg-white p-4 rounded-lg"
                        >
                            {!showAmountEtat ? (
                                <EtatsChart data={filteredData} title="Répartition des Déclarations par Etats" />
                            ) : (
                                <MontantChart
                                    data={filteredData}
                                    groupBy="Etat"
                                    title="Répartition des Montants par Etat"
                                />

                                // <MontantChart data={filteredData} groupBy="Etat" title="Répartition des Montants par Etat" />
                            )}
                            <div className="flex justify-between items-center">
                                <div class="flex items-center gap-2 label:">
                                    <label> Nombre </label>
                                    <Switcher isChecked={showAmountEtat} setIsChecked={setShowAmountEtat} />
                                    <label> Montant </label>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
                <div className="lg:flex flex-col justify-between mt-4 md:mt-6 lg:mt-0 lg:ml-4 xl:ml-8 lg:w-1/2">
                    <div className="relative w-full">
                        <motion.div
                            className="bg-white p-4 rounded-lg"
                        >
                            {!showAmountMotif ? (
                                <EtatsChart data={filteredData.filter(declaration => declaration.Etat_Motif != "")} valeur="Etat_Motif" title="Répartition des Déclarations par Motifs" montant={showAmountMotif ? "Montant_Du_Definitif" : null} />
                            ) : (
                                <MontantChart
                                    data={filteredData}
                                    groupBy="Etat_Motif"
                                    title="Répartition des Montants par Etat"
                                />
                                // <MontantChart data={filteredData} groupBy="Etat" title="Répartition des Montants par Etat" />
                            )}
                            <div className="flex justify-between items-center">
                                <div class="flex items-center gap-2 label:">
                                    <label> Nombre </label>
                                    <Switcher isChecked={showAmountMotif} setIsChecked={setShowAmountMotif} />
                                    <label> Montant </label>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                    <div className="sm:flex justify-between items-center gap-x-6 xl:gap-x-8 mt-4 md:mt-6">
                        <div className="relative w-full">
                            <div className="flex flex-col justify-center items-center gap-2 bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 p-4 rounded-lg w-full h-60 text-white">
                                <h6 class="mt-2 font-bold text-white text-3xl leading-8">
                                    {loadingD ? <div class="bg-gray-100 rounded-md w-36 h-8 animate-pulse"></div>
                                        : formatCurrency(Math.round(filteredData.reduce((acc, declaration) => {
                                            return acc + (declaration.Cout_tt_Pathologie || 0);
                                        }, 0)))}
                                </h6>
                                <p class="font-medium text-sm text-center uppercase tracking-widest">
                                    Montant Totaux des Pathologies
                                </p>
                            </div>
                        </div>
                        <div className="relative mt-4 sm:mt-0 w-full">
                            <div className="flex flex-col justify-center items-center gap-2 bg-white bg-gradient-to-tr from-orange-600 via-orange-500 to-orange-400 p-4 p-4 rounded-lg w-full h-60 text-white">
                                <h6 class="mt-2 font-bold text-white text-3xl leading-8">
                                    {loadingD ? <div class="bg-gray-100 rounded-md w-36 h-8 animate-pulse"></div>
                                        : formatCurrency(Math.round(filteredData.reduce((acc, declaration) => {
                                            return acc + (declaration.Montant_Du_Definitif || 0);
                                        }, 0)))}
                                </h6>
                                <p class="font-medium text-sm text-center uppercase tracking-widest">
                                    Montant Totaux des Déclarations
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="lg:flex items-stretch mt-4 md:mt-8">
                <div className="bg-white p-4 rounded-lg w-full">
                    <DoubleColumnChartDecla id="DoubleChart03" data={top15CoutDeclas} title="Top 15 Coûts Déclarations & Pathologies par Déclaration" />
                </div>
            </div>
        </>
    );
}

export default DeclarationTab;